import { type ElementType } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import { PolymorphicProps } from "shared/types";
import { cn } from "shared/utils/cn";

import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "relative cursor-pointer inline-flex rounded-xl items-center justify-center h-13.5 px-5 transition-colors gap-x-2 outline-none",
    "font-medium",
    "hover:brightness-105 active:brightness-105 disabled:opacity-50 disabled:pointer-events-none",
  ],
  {
    variants: {
      theme: {
        default: "persian-orange-gradient text-white",
      },
      outline: {
        true: "border",
        false: null,
      },
      disabled: {
        true: "opacity-50 pointer-events-none",
        false: null,
      },
    },
    compoundVariants: [
      {
        theme: "default",
        outline: true,
        className:
          "bg-transparent !bg-none border-atomicTangerine text-balticSea",
      },
    ],
    defaultVariants: {
      theme: "default",
      outline: false,
    },
  }
);

type Props = VariantProps<typeof buttonVariants> & {
  isLoading?: boolean;
  spinnerClassName?: string;
};

export type ButtonProps<E extends ElementType = "button"> = PolymorphicProps<
  E,
  Props
>;

const Button = <E extends ElementType = "button">(props: ButtonProps<E>) => {
  const {
    className,
    theme = "default",
    isLoading,
    children,
    as = "button",
    spinnerClassName,
    disabled,
    outline,
    ...restProps
  } = props;
  const Comp = as;
  return (
    <Comp
      type="button"
      className={cn(buttonVariants({ theme, outline, disabled }), className)}
      disabled={disabled || isLoading}
      {...restProps}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner className={spinnerClassName} />
        </span>
      )}
      {isLoading ? <span className="opacity-0">{children}</span> : children}
    </Comp>
  );
};
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
